/**
 * Filter controls for the Price Comparison Table.
 *
 * Horizontal bar rendered above the table containing:
 *   - Records-per-page selector (10, 50, 100, 200, 400, All)
 *   - Text search with 300 ms debounce
 *   - Competitor column filter (checkbox dropdown)
 *   - Key Lines checkbox (the sponsor's focus products from the master sheet)
 *   - Stock / availability filter checkboxes
 *   - CSV export button
 *
 * All filter values are controlled by the parent (ComparisonPage) via
 * the ``filters`` prop, with changes propagated through ``onFilterChange``.
 *
 * Spec reference: §6.8 (Frontend: Price Comparison Table Tab).
 */
import { useEffect, useRef, useState } from 'react';
import { domainName } from '../utils/domains';
import RefreshOurPricesButton from './RefreshOurPricesButton';

/** Records-per-page options matching the backend ``VALID_LIMITS``. */
const LIMIT_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '400', label: '400' },
  { value: 'all', label: 'All' },
];

/**
 * CSV export ranges matching the backend ``VALID_EXPORT_HISTORY_DAYS``.
 * ``days: null`` exports the current-state snapshot (original behaviour).
 */
const EXPORT_OPTIONS = [
  { days: null, label: 'Current data' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 60, label: 'Last 60 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 180, label: 'Last 180 days' },
];

// ---------------------------------------------------------------------------
// Competitor dropdown
// ---------------------------------------------------------------------------

/**
 * Dropdown with a checkbox per competitor domain.
 *
 * Shows a count badge (e.g. "5 / 15") on the toggle button.
 * Includes "Select all" and "Clear all" quick-action buttons.
 */
function CompetitorDropdown({ allCompetitors, selectedSet, onToggle, onSelectAll, onClearAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const total = allCompetitors.length;
  const count = selectedSet.size;
  const allSelected = count === total;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="inline-flex items-center gap-1.5 rounded-md border
                   border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700
                   shadow-sm hover:bg-gray-50 transition-colors"
      >
        {/* Filter icon */}
        <svg
          className="h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917
               1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432
               5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25
               0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0
               0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3
               5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32
               0 0 1 12 3Z"
          />
        </svg>
        Competitors
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs
                         font-medium text-gray-500">
          {allSelected ? 'All' : `${count}/${total}`}
        </span>
        {/* Chevron */}
        <svg
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-60
                     max-w-[calc(100vw-2rem)] rounded-lg border
                     border-gray-200 bg-white shadow-lg z-50"
        >
          {/* Quick actions */}
          <div className="flex items-center justify-between border-b
                          border-gray-100 px-3 py-2">
            <button
              type="button"
              onClick={onSelectAll}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>

          {/* Checkbox list */}
          <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
            {allCompetitors.map((id) => (
              <label
                key={id}
                className="flex items-center gap-2 rounded px-2 py-1.5
                           text-sm text-gray-700 hover:bg-gray-50
                           cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(id)}
                  onChange={() => onToggle(id)}
                  className="h-3.5 w-3.5 rounded border-gray-300
                             text-blue-600 focus:ring-blue-500"
                />
                {domainName(id)}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Errors dropdown
// ---------------------------------------------------------------------------

/**
 * Error-type options matching the backend ``error_reasons`` tokens.
 * The fixable ones need action in the master scraper sheet (or on our
 * selectors); transient ones self-heal on the next run.
 */
const ERROR_REASON_OPTIONS = [
  { value: 'product_gone', label: 'Errors (fixable - url)' },
  { value: 'variant_mismatch', label: 'Errors (fixable - variant)' },
  { value: 'invalid_price', label: 'Errors (fixable - price)' },
  { value: 'transient', label: 'Errors (transient)' },
];

/**
 * Dropdown replacing the old "Has Errors" checkbox.
 *
 * A master "Errors (all)" checkbox switches the errors-only view on/off
 * (same row-filter semantics as the old checkbox — off by default).
 * Four error-type sub-checkboxes, all ticked by default, narrow the
 * view to the selected error types; unticking every type switches the
 * master off. Errors recorded before type tagging existed match any
 * selection.
 */
function ErrorsDropdown({ hasErrors, errorReasons, onFilterChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // No error_reasons param = all types selected
  const selectedReasons = (() => {
    if (!errorReasons) {
      return new Set(ERROR_REASON_OPTIONS.map((o) => o.value));
    }
    return new Set(errorReasons.split(',').filter(Boolean));
  })();

  function toggleMaster(checked) {
    // Turning the master on resets to all types; off clears everything.
    onFilterChange({ has_errors: checked, error_reasons: undefined });
  }

  function toggleReason(value) {
    const next = new Set(selectedReasons);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    if (next.size === 0) {
      // Nothing selected = no error filtering at all
      onFilterChange({ has_errors: false, error_reasons: undefined });
      return;
    }
    if (next.size === ERROR_REASON_OPTIONS.length) {
      onFilterChange({ has_errors: true, error_reasons: undefined });
    } else {
      const ordered = ERROR_REASON_OPTIONS
        .map((o) => o.value)
        .filter((v) => next.has(v));
      onFilterChange({ has_errors: true, error_reasons: ordered.join(',') });
    }
  }

  const allSelected = selectedReasons.size === ERROR_REASON_OPTIONS.length;
  const badge = !hasErrors
    ? 'Off'
    : allSelected
      ? 'All'
      : `${selectedReasons.size}/${ERROR_REASON_OPTIONS.length}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="inline-flex items-center gap-1.5 rounded-md border
                   border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700
                   shadow-sm hover:bg-gray-50 transition-colors"
      >
        Errors
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
            hasErrors
              ? 'bg-purple-100 text-purple-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {badge}
        </span>
        {/* Chevron */}
        <svg
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-64
                     max-w-[calc(100vw-2rem)] rounded-lg border
                     border-gray-200 bg-white shadow-lg z-50"
        >
          {/* Master toggle */}
          <label
            className="flex items-center gap-2 border-b border-gray-100
                       px-3 py-2 text-sm font-medium text-gray-700
                       hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={hasErrors}
              onChange={(e) => toggleMaster(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300
                         text-blue-600 focus:ring-blue-500"
            />
            Errors (all)
          </label>

          {/* Error-type list */}
          <div className="p-2 space-y-0.5">
            {ERROR_REASON_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-2 rounded px-2 py-1.5
                           text-sm hover:bg-gray-50 cursor-pointer ${
                             hasErrors ? 'text-gray-700' : 'text-gray-400'
                           }`}
              >
                <input
                  type="checkbox"
                  disabled={!hasErrors}
                  checked={hasErrors && selectedReasons.has(opt.value)}
                  onChange={() => toggleReason(opt.value)}
                  className="h-3.5 w-3.5 rounded border-gray-300
                             text-blue-600 focus:ring-blue-500
                             disabled:opacity-50"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main FilterPanel
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Export dropdown
// ---------------------------------------------------------------------------

/**
 * CSV export button with a range menu.
 *
 * Offers the current-state snapshot plus 7/30/60/90/180-day
 * historical windows; selecting an option calls ``onExport`` with
 * the chosen day count (or null for current data).
 */
function ExportDropdown({ onExport, exporting, mobileOpen }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div
      className={`relative ${mobileOpen ? 'block' : 'hidden md:block'} order-3 md:order-none`}
      ref={ref}
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        disabled={exporting}
        className="inline-flex items-center gap-1.5 rounded-md border
                   border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700
                   shadow-sm hover:bg-gray-50 disabled:opacity-50
                   disabled:cursor-not-allowed transition-colors"
      >
        {exporting ? (
          <svg
            className="h-4 w-4 animate-spin text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2
                 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25
                 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0
                 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        )}
        {exporting ? 'Exporting…' : 'Export CSV'}
        <svg
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-1 w-44
                     max-w-[calc(100vw-2rem)] rounded-lg border
                     border-gray-200 bg-white shadow-lg z-50 py-1"
        >
          {EXPORT_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => {
                setOpen(false);
                onExport(opt.days);
              }}
              className={`block w-full px-3 py-1.5 text-left text-sm
                         text-gray-700 hover:bg-gray-50 ${
                           opt.days === null ? 'border-b border-gray-100' : ''
                         }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Filter panel above the comparison table.
 *
 * @param {Object}   props
 * @param {Object}   props.filters          Current filter state.
 * @param {string[]} props.allCompetitors   All available competitor domain IDs.
 * @param {Function} props.onFilterChange   Callback: ``(partialUpdate) => void``.
 * @param {Function} props.onExport         Callback: ``(historyDays|null) => void``.
 * @param {boolean}  props.exporting        True while CSV export is in progress.
 * @param {number}   [props.keyLineCount]   Key-line products in the full set
 *                                          (shown on the Key Lines label).
 */
export default function FilterPanel({
  filters,
  allCompetitors,
  onFilterChange,
  onExport,
  exporting,
  rememberCompetitors,
  onRememberCompetitorsChange,
  keyLineCount,
  onOurPricesRefreshed,
}) {
  // ---------- Mobile collapse ----------
  // Below md only the search box and a "Filters" toggle stay visible;
  // the rest of the toolbar expands on demand. At md+ the toggle is
  // hidden and every control shows, preserving the desktop layout.
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeFilterCount =
    (filters.competitors ? 1 : 0) +
    (filters.key_lines ? 1 : 0) +
    (filters.our_in_stock ? 1 : 0) +
    (filters.competitor_in_stock ? 1 : 0) +
    (filters.competitor_oos ? 1 : 0) +
    (filters.competitor_has_price ? 1 : 0) +
    (filters.has_errors ? 1 : 0);

  // ---------- Search (local state + debounce) ----------
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      const val = searchInput.trim() || undefined;
      if (val !== (filters.search || undefined)) {
        onFilterChange({ search: val });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function clearSearch() {
    setSearchInput('');
    onFilterChange({ search: undefined });
  }

  // ---------- Competitors ----------
  const selectedSet = (() => {
    if (!filters.competitors) return new Set(allCompetitors);
    return new Set(filters.competitors.split(',').filter(Boolean));
  })();

  function toggleCompetitor(id) {
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    if (next.size === 0) return; // don't allow empty
    if (next.size === allCompetitors.length) {
      onFilterChange({ competitors: undefined });
    } else {
      onFilterChange({ competitors: [...next].join(',') });
    }
  }

  function selectAllCompetitors() {
    onFilterChange({ competitors: undefined });
  }

  function clearAllCompetitors() {
    // Keep at least one
    if (allCompetitors.length > 0) {
      onFilterChange({ competitors: allCompetitors[0] });
    }
  }

  // ---------- Render ----------
  // Mobile hides the collapsible controls via `hidden md:...`; order-
  // utilities put search + toggle first on phones without moving any
  // DOM, so the md+ layout is byte-identical to the original.
  const collapsedFlex = mobileOpen ? 'flex' : 'hidden md:flex';

  return (
    <div className="flex flex-wrap items-center gap-3 pb-3">
      {/* Records selector */}
      <div className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5`}>
        <label htmlFor="limit-select" className="text-sm text-gray-600">
          Show:
        </label>
        <select
          id="limit-select"
          value={filters.limit}
          onChange={(e) => onFilterChange({ limit: e.target.value })}
          className="rounded-md border-gray-300 py-1.5 pl-2 pr-7 text-sm
                     shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          {LIMIT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Search — always visible; first control on mobile */}
      <div className="relative order-1 md:order-none flex-1 min-w-0 md:min-w-[180px] max-w-none md:max-w-xs">
        {/* Search icon */}
        <svg
          className="pointer-events-none absolute left-2.5 top-1/2
                     -translate-y-1/2 h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196
               5.196a7.5 7.5 0 0 0 10.607 10.607Z"
          />
        </svg>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={'Search products\u2026'}
          className="w-full rounded-md border-gray-300 py-1.5 pl-8 pr-8
                     text-sm shadow-sm placeholder:text-gray-400
                     focus:border-blue-500 focus:ring-blue-500"
        />
        {searchInput && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2
                       text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Mobile-only Filters toggle */}
      <button
        type="button"
        onClick={() => setMobileOpen((p) => !p)}
        className="order-2 md:hidden inline-flex items-center gap-1.5
                   rounded-md border border-gray-300 bg-white px-3 py-1.5
                   text-sm text-gray-700 shadow-sm hover:bg-gray-50
                   transition-colors"
      >
        Filters
        {activeFilterCount > 0 && (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs
                           font-medium text-blue-700">
            {activeFilterCount}
          </span>
        )}
        <svg
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
            mobileOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Competitor filter */}
      {allCompetitors.length > 0 && (
        <div className={`${mobileOpen ? 'block' : 'hidden md:block'} order-3 md:order-none`}>
          <CompetitorDropdown
            allCompetitors={allCompetitors}
            selectedSet={selectedSet}
            onToggle={toggleCompetitor}
            onSelectAll={selectAllCompetitors}
            onClearAll={clearAllCompetitors}
          />
        </div>
      )}

      {/* Key lines (defined in the master sheet; read-only here) */}
      <label
        className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-sm text-gray-600
                    cursor-pointer select-none whitespace-nowrap`}
        title="Only products flagged in the master sheet's Key Lines column"
      >
        <input
          type="checkbox"
          checked={filters.key_lines || false}
          onChange={(e) => onFilterChange({ key_lines: e.target.checked })}
          className="h-3.5 w-3.5 rounded border-amber-400 text-amber-600
                     focus:ring-amber-500"
        />
        Key Lines
        {typeof keyLineCount === 'number' && (
          <span className="text-xs text-gray-400">({keyLineCount})</span>
        )}
      </label>

      {/* Stock filters */}
      <label className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-sm text-gray-600
                        cursor-pointer select-none whitespace-nowrap`}>
        <input
          type="checkbox"
          checked={filters.our_in_stock || false}
          onChange={(e) => onFilterChange({ our_in_stock: e.target.checked })}
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600
                     focus:ring-blue-500"
        />
        Our In Stock
      </label>

      <label className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-sm text-gray-600
                        cursor-pointer select-none whitespace-nowrap`}>
        <input
          type="checkbox"
          checked={filters.competitor_in_stock || false}
          onChange={(e) =>
            onFilterChange({ competitor_in_stock: e.target.checked })
          }
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600
                     focus:ring-blue-500"
        />
        Comp. In Stock
      </label>

      <label className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-sm text-gray-600
                        cursor-pointer select-none whitespace-nowrap`}>
        <input
          type="checkbox"
          checked={filters.competitor_oos || false}
          onChange={(e) =>
            onFilterChange({ competitor_oos: e.target.checked })
          }
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600
                     focus:ring-blue-500"
        />
        Comp. OOS
      </label>

      <label className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-sm text-gray-600
                        cursor-pointer select-none whitespace-nowrap`}>
        <input
          type="checkbox"
          checked={filters.competitor_has_price || false}
          onChange={(e) =>
            onFilterChange({ competitor_has_price: e.target.checked })
          }
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600
                     focus:ring-blue-500"
        />
        Has Price
      </label>

      {/* Errors filter (master + per-type dropdown) */}
      <div className={`${mobileOpen ? 'block' : 'hidden md:block'} order-3 md:order-none`}>
        <ErrorsDropdown
          hasErrors={filters.has_errors || false}
          errorReasons={filters.error_reasons}
          onFilterChange={onFilterChange}
        />
      </div>

      {/* Remember competitor filter */}
      {onRememberCompetitorsChange && (
        <label className={`${collapsedFlex} order-3 md:order-none items-center gap-1.5 text-xs text-gray-500
                          cursor-pointer select-none whitespace-nowrap ml-2
                          border-l border-gray-200 pl-3`}>
          <input
            type="checkbox"
            checked={rememberCompetitors || false}
            onChange={(e) => onRememberCompetitorsChange(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600
                       focus:ring-blue-500"
          />
          Remember filters
        </label>
      )}

      {/* Spacer */}
      <div className="hidden md:block flex-1" />

      {/* Our prices from the store feed: as-of label + on-demand refresh */}
      <RefreshOurPricesButton
        onRefreshed={onOurPricesRefreshed}
        mobileOpen={mobileOpen}
      />

      {/* Export CSV (current data or historical range) */}
      <ExportDropdown
        onExport={onExport}
        exporting={exporting}
        mobileOpen={mobileOpen}
      />
    </div>
  );
}
